import { AxiosError, AxiosRequestConfig, AxiosRequestHeaders } from 'axios';
import { z } from 'zod';
import { NotAuthorizedClient } from './not-authorized-client';
import { HttpConnection, RequestRetryLimitExceededException } from './http-connection';
import { AuthService } from '~/app-modules/auth/services/auth-service';
import { FileResponse } from '~/app-modules/core/clients/dto/file-response';
import { UserStore } from '~/app-modules/user/stores/user.store';

export class AuthorizedClient extends NotAuthorizedClient {
  private readonly _businessAreaIdHeaderName = 'X-FS-Business-Area-Id';

  constructor(
    httpConnection: HttpConnection,
    private authService: AuthService,
    private userStore: UserStore,
  ) {
    super(httpConnection);
  }

  protected executeRequest<T>(
    options: AxiosRequestConfig,
    deserializer: z.Schema<T> = z.optional(z.any()),
  ): Promise<T> {
    return this.executeAuthorizedRequest(
      async (authToken) =>
        await super.executeRequest(
          { ...options, headers: this.getHeaders(authToken) },
          deserializer,
        ),
    );
  }

  protected downloadFile(options: AxiosRequestConfig): Promise<FileResponse> {
    return this.executeAuthorizedRequest(
      async (authToken) =>
        await super.downloadFile({ ...options, headers: this.getHeaders(authToken) }),
    );
  }

  private async executeAuthorizedRequest<T>(
    executeRequest: (authToken: string) => Promise<T>,
  ): Promise<T> {
    let tryCount = 1;

    while (tryCount <= 2) {
      try {
        const authToken = this.authService.Bearer;

        return await executeRequest(authToken);
      } catch (e) {
        if (this.isUnAuthorizedException(e)) {
          await this.authService.ensureTokensRefreshed();
          tryCount++;
          continue;
        }
        throw e;
      }
    }
    throw new RequestRetryLimitExceededException();
  }

  private isUnAuthorizedException(e: unknown) {
    return e instanceof AxiosError && e.response?.status == 401;
  }

  private getHeaders(authToken: string): AxiosRequestHeaders {
    const headers: Record<string, string> = { Authorization: authToken };
    const businessAreaId = this.userStore.currentBusinessArea?.id;

    if (businessAreaId) {
      headers[this._businessAreaIdHeaderName] = businessAreaId;
    }

    return headers;
  }
}
